import { CurrencyPipe, NgClass, isPlatformBrowser } from '@angular/common'
import type { AfterViewInit, OnInit } from '@angular/core'
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DOCUMENT,
  DestroyRef,
  HostBinding,
  HostListener,
  PLATFORM_ID,
  computed,
  inject,
  signal,
  viewChild,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import type { ResolveFn } from '@angular/router'
import { ActivatedRoute, Router } from '@angular/router'

import { TranslatePipe, TranslateService } from '@ngx-translate/core'
import { catchError, map, of, startWith, switchMap, take, tap } from 'rxjs'

import { environment } from '../../../../environments/environment'
import { ArticlesService } from '../../../api/articles-service/articles.service'
import { hasGraphQLErrorStatus } from '../../../api/graphql-error-status'
import {
  type ArticleDetailsDto,
  type ArticleFootnote,
} from '../../../api/responses'
import { AdvertisementComponent } from '../../../components/advertisement/advertisement.component'
import { ArticleMetaData } from '../../../components/article-list/components/article-meta-data'
import { BreadcrumbsComponent } from '../../../components/breadcrumbs/breadcrumbs.component'
import { ErrorToastComponent } from '../../../components/error-toast/error-toast.component'
import { SubscribeButtonComponent } from '../../../components/subscribe-button/subscribe-button.component'
import { AuthService } from '../../../services/auth/auth.service'
import { HtmlContentPipe } from '../../../shared/pipes/html-content.pipe'
import { LocalizedDatePipe } from '../../../shared/pipes/localized-date.pipe'
import { ProcessContentPipe } from '../../../shared/pipes/process-content.pipe'
import { ProcessFootnotesPipe } from '../../../shared/pipes/process-footnotes.pipe'
import type { SeoRouteMeta } from '../../../shared/seo/seo.types'
import { firstImage, firstText } from '../../../shared/seo/seo.util'
import { calculateArticlePurchaseTotalAmount } from '../../../shared/utils/article-purchase-price.utils'
import {
  type ArticlePurchasePrice,
  ArticleWithNullableRefs,
  BreadcrumbItem,
  ContentAccessEnum,
  hasFullTextAccess,
  IndexItem,
} from '../../../types/types'
import { articleCitation } from './article-citation'
import { ArticleActionButtonsComponent } from './components/article-action-buttons/article-action-buttons.component'
import { FootnoteReferenceComponent } from './components/footnote-reference/footnote-reference.component'
import { IndexComponent } from './components/index/index.component'

type ArticleFootnoteType = 'title' | 'subtitle' | 'author'

interface FootnoteReference {
  number: number
  content: string
}

// Fetching in a resolver (rather than the component's ngOnInit) matters for
// SSR: the router only considers navigation complete once resolvers emit, so
// this is guaranteed to be awaited before the page is rendered/captured. A
// fetch started from ngOnInit races the server's render-and-destroy cycle and
// can be torn down mid-request before it ever emits a value, which is why
// the SSR output used to show a permanent loading state for article pages.
export const articleDetailsResolver: ResolveFn<
  ArticleDetailsDto['Article'] | null
> = route => {
  const articlesService = inject(ArticlesService)
  const authService = inject(AuthService)
  const isBrowser = isPlatformBrowser(inject(PLATFORM_ID))

  const id = route.paramMap.get('id')
  const articleId = id ? parseInt(id, 10) : NaN
  if (isNaN(articleId)) {
    return of(null)
  }

  const article$ = articlesService
    .getArticleDetails(articleId, 'network-only')
    .pipe(
      map(details => details ?? null),
      catchError((error: unknown) => {
        console.error('Error fetching article details:', error)
        // A missing article is normal flow; anything else leaves the page blank.
        const cause = error instanceof Error ? error.cause : undefined
        if (!hasGraphQLErrorStatus(cause, 404)) {
          authService.captureError(error, 'article_failed')
        }
        return of(null)
      })
    )
  if (!isBrowser) {
    return article$
  }
  // A link visit always lands here, so its outcome is checked here.
  return article$.pipe(
    switchMap(article =>
      authService.waitForAuthCheckCompletion().pipe(
        tap(() => {
          authService.checkLinkArticle(article)
        }),
        map(() => article)
      )
    )
  )
}

/**
 * Head metadata for an article, from the `meta` group the API's `seoPlugin`
 * maintains. Registered as route data (`data: { seo: articleSeo }`) so
 * `SeoTitleStrategy` can apply it as part of the navigation that resolved the
 * article - see seo-title.strategy.ts.
 */
export const articleSeo: SeoRouteMeta = route => {
  const article = route.data['articleDetails'] as
    ArticleDetailsDto['Article'] | null | undefined
  if (!article) return null

  return {
    title: firstText(article.meta?.title, article.title),
    description: firstText(article.meta?.description, article.subtitle),
    image: firstImage(article.meta?.image),
    // The same article is reachable from five routes (its own, plus via
    // magazine, edition, author and editor); /articles/:id is the one to index.
    canonicalPath: `/articles/${String(article.id)}`,
    type: 'article',
    publishedTime: article.published_at,
    citation: articleCitation(article),
  }
}

@Component({
  selector: 'dhp-article-details',
  templateUrl: './article-details.component.html',
  styleUrls: ['./article-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AdvertisementComponent,
    TranslatePipe,
    HtmlContentPipe,
    ProcessContentPipe,
    ProcessFootnotesPipe,
    ArticleMetaData,
    NgClass,
    LocalizedDatePipe,
    CurrencyPipe,
    BreadcrumbsComponent,
    IndexComponent,
    ArticleActionButtonsComponent,
    SubscribeButtonComponent,
    ErrorToastComponent,
    FootnoteReferenceComponent,
  ],
})
export class ArticleDetailsComponent implements OnInit, AfterViewInit {
  private readonly route = inject(ActivatedRoute)
  private readonly articlesService = inject(ArticlesService)
  private readonly destroyRef = inject(DestroyRef)
  private readonly authService = inject(AuthService)
  private readonly changeDetectorRef = inject(ChangeDetectorRef)
  private document = inject<Document>(DOCUMENT)
  private platformId = inject(PLATFORM_ID)
  private readonly router = inject(Router)
  private readonly translateService = inject(TranslateService)

  articleDetails?: ArticleDetailsDto['Article']
  content = ''
  isIndexExpanded = false
  isSelectionAndEssence = false
  /**
   * Headings to jump to. `null` until they have been fetched, and empty for an
   * article that has none.
   */
  readonly indexItems = signal<IndexItem[] | null>(null)
  /**
   * Whether to leave room for the index rail beside the content.
   *
   * Still loading counts as "yes": most articles have an index, and reserving
   * the rail up front keeps their content from jumping sideways once the
   * headings arrive. An article that turns out to have none gives the room
   * back, instead of keeping an empty white strip beside it forever.
   */
  readonly hasIndex = computed(() => {
    const items = this.indexItems()
    return items === null || items.length > 0
  })
  loading = true
  error: string | null = null
  breadcrumbItems: BreadcrumbItem[] = []
  isAuthenticated = false
  isAuthCheckCompleted = false
  isDesktopBrowser = false
  isSubscribedAndSeated = false
  hasArticleAccess = false
  canDownloadArticle = false
  initializationComplete = false
  activeSection: string | null = null
  isHeaderHidden = false
  subscriptionButtonDetail: string | null = null

  readonly environment = environment

  @HostBinding('attr.ngSkipHydration') readonly ngSkipHydration = 'true'

  readonly articleIndex = viewChild<IndexComponent>('articleIndex')

  ngOnInit(): void {
    this.loading = true
    this.isAuthCheckCompleted = this.authService.authCheckCompleted()
    this.updateAccessState()

    this.authService.authCheckCompleted$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(completed => {
        this.isAuthCheckCompleted = completed
        this.updateAccessState()
      })

    // Subscribe to auth changes to reset loading state when user logs in
    this.authService.currentUser$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.updateAccessState()
      })

    this.route.paramMap
      .pipe(
        switchMap(paramMap => {
          const id = paramMap.get('id')
          const articleId = id ? parseInt(id, 10) : NaN
          if (isNaN(articleId)) return of<IndexItem[] | null>([])

          // Back to "not fetched yet" first: otherwise the previous article's
          // headings stay in the index while the new ones load.
          return this.articlesService
            .getArticleIndex(articleId)
            .pipe(startWith(null))
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(items => {
        this.indexItems.set(items)
      })

    // Resolved by articleDetailsResolver - already available on the very
    // first emission (including during SSR), and re-emits when navigating to
    // a different article id while this component instance is reused.
    this.route.data
      .pipe(
        map(
          data =>
            (data['articleDetails'] as ArticleDetailsDto['Article'] | null) ??
            null
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(details => {
        this.applyArticleDetails(details)
      })
  }

  ngAfterViewInit(): void {
    // Only execute browser-specific code when in browser environment
    if (isPlatformBrowser(this.platformId)) {
      this.updateViewportState()
      this.refreshIndexState()

      // Disable scroll restoration to avoid jumps when navigating
      history.scrollRestoration = 'manual'
    }
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.updateViewportState()
  }

  updateAccessState(): void {
    this.isAuthenticated = this.authService.isAuthenticated()

    if (!this.isAuthCheckCompleted) {
      this.isSubscribedAndSeated = false
      this.changeDetectorRef.markForCheck()
      return
    }

    this.isSubscribedAndSeated =
      this.articleDetails?.user_access === ContentAccessEnum.SEAT_ASSIGNED
    this.hasArticleAccess = hasFullTextAccess(
      this.articleDetails?.user_access ?? null
    )
    this.canDownloadArticle =
      this.isSubscribedAndSeated && !!this.articleDetails?.pdf

    this.changeDetectorRef.markForCheck()
  }

  private applyArticleDetails(
    details: ArticleDetailsDto['Article'] | null
  ): void {
    this.error = null
    this.articleDetails = undefined
    this.content = ''
    this.isSubscribedAndSeated = false
    this.hasArticleAccess = false
    this.canDownloadArticle = false
    this.initializationComplete = false

    if (details) {
      this.articleDetails = details
      this.content = Array.isArray(details.content)
        ? details.content.join('')
        : typeof details.content === 'object'
          ? Object.values(details.content).join('')
          : details.content

      this.isSelectionAndEssence =
        details.magazine?.selectionAndEssence ?? false
      // Always update breadcrumbs regardless of content
      this.updateBreadcrumbs()

      // Only generate index if content exists
      if (this.content && this.content.trim() !== '') {
        this.generateIndexItemsFromContent(this.content)
      }

      this.initializationComplete = true
      this.loading = false
      this.updateAccessState()
      this.refreshIndexState()
    } else {
      this.error = 'Article details not found'
      this.loading = false
      this.initializationComplete = true // Ensure loading stops
    }

    this.changeDetectorRef.markForCheck()
  }

  generateIndexItemsFromContent(content: string): void {
    // Skip if not in a browser environment or no content
    if (!isPlatformBrowser(this.platformId) || !content) {
      return
    }

    const tempDiv = this.document.createElement('div')
    tempDiv.innerHTML = content
    const headings = tempDiv.querySelectorAll('h1, h2, h3, h4, h5, h6')

    Array.from(headings).forEach((heading, index) => {
      const headingId = `heading-${String(index)}`
      heading.setAttribute('id', headingId)
    })

    this.refreshIndexState()
  }

  updateBreadcrumbs(): void {
    if (this.articleDetails) {
      const magazine = this.articleDetails.magazine
      const edition = this.articleDetails.edition
      const articleTitle = this.articleDetails.title

      this.translateService
        .get(['routing.magazines', 'routing.detail', 'routing.edition'])
        .pipe(take(1))
        .subscribe(() => {
          // Start with common breadcrumbs
          const breadcrumbs: BreadcrumbItem[] = [
            {
              label: 'components.breadcrumbs.magazines',
              url: '/magazines',
            },
          ]

          if (magazine) {
            breadcrumbs.push({
              label: magazine.abbreviation ?? magazine.title,
              url: `/magazines/detail/${String(magazine.id)}/${String(magazine.slug)}`,
            })
          }

          // Only add the edition breadcrumb if it's not a Selection and Essence magazine
          if (magazine && !this.isSelectionAndEssence) {
            breadcrumbs.push({
              label: 'components.breadcrumbs.editionWithDetails',
              url: `/magazines/detail/${String(magazine.id)}/${String(magazine.slug)}/edition/${String(edition.id)}`,
              params: {
                year: edition.year?.toString() ?? '',
                version: edition.edition_version,
              },
            })
          }

          // Add the article breadcrumb
          breadcrumbs.push({
            label: articleTitle,
            url: '',
          })

          this.breadcrumbItems = breadcrumbs
          this.changeDetectorRef.detectChanges()
        })
    }
  }

  onIndexItemClicked(link: string): void {
    const id = link.startsWith('#') ? link.slice(1) : link
    this.scrollToElement(id)
    this.isIndexExpanded = false
  }

  async onDownload(): Promise<void> {
    if (!this.articleDetails) {
      return
    }

    if (!this.canDownloadArticle) {
      return
    }

    if (!this.isAuthenticated) {
      await this.navigateToLogin()
      return
    }

    this.proceedWithDownload()
  }

  onPrint(): void {
    if (this.articleDetails && this.hasArticleAccess) {
      window.print()
    }
  }

  async goToPurchaseCheckout(): Promise<void> {
    if (!this.articleDetails) {
      return
    }

    await this.router.navigate([
      '/articles',
      this.articleDetails.id,
      'purchase',
    ])
  }

  async onShare(): Promise<void> {
    // Skip if not in a browser environment
    if (!isPlatformBrowser(this.platformId) || !this.articleDetails) {
      return
    }

    try {
      await navigator.share({
        title: this.articleDetails.title,
        text: 'Check out this article!',
        url: window.location.href,
      })
    } catch (error) {
      if ((error as Error).name !== 'AbortError') {
        console.error('Error sharing:', error)
      }
    }
  }

  getTitleSizeClass(title: string | undefined): string {
    if (!title) return 'text-4xl'

    const length = title.length
    let result

    if (length <= 30) {
      result = 'text-4xl' // Very short titles (biggest)
    } else if (length <= 50) {
      result = 'text-4xl' // Short titles
    } else if (length <= 75) {
      result = 'text-4xl' // Medium titles
    } else if (length <= 100) {
      result = 'text-4xl' // Long titles
    } else if (length <= 150) {
      result = 'text-3xl' // Very long titles
    } else {
      result = 'text-3xl' // Extremely long titles (smallest)
    }

    return result
  }

  // Returns subtitle in proportional size to the title
  getSubtitleSizeClass(title: string | undefined): string {
    if (!title) return 'text-xl'

    const length = title.length
    let result

    if (length <= 30) {
      result = 'text-2xl' // Very short titles
    } else if (length <= 50) {
      result = 'text-2xl' // Short titles
    } else if (length <= 75) {
      result = 'text-2xl' // Medium titles
    } else if (length <= 100) {
      result = 'text-2xl' // Long titles
    } else if (length <= 150) {
      result = 'text-xl' // Very long titles
    } else {
      result = 'text-xl' // Extremely long titles (smallest)
    }

    return result
  }

  async navigateToLogin(): Promise<void> {
    const returnUrl = window.location.pathname
    await this.router.navigate(['/auth/login'], {
      queryParams: { returnUrl },
    })
  }

  getAllFootnotes(): ArticleFootnote[] {
    return this.articleDetails?.footnotes ?? []
  }

  /**
   * Looks up the leading footnote of the given type. The number matches the
   * position in the rendered footnotes list, which is what the `footnote-<n>`
   * anchors point at.
   */
  getFootnoteReference(
    footnoteType: ArticleFootnoteType
  ): FootnoteReference | undefined {
    const footnotes = this.getAllFootnotes()
    const footnote = footnotes.find(
      candidate => candidate.footnoteType === footnoteType
    )

    if (!footnote) {
      return undefined
    }

    return {
      number: footnotes.indexOf(footnote) + 1,
      content: footnote.content,
    }
  }

  getArticlePurchasePrice(): ArticlePurchasePrice | null {
    return this.articleDetails?.purchase_price ?? null
  }

  getArticlePurchaseTotal(): number | null {
    const purchasePrice = this.getArticlePurchasePrice()
    return calculateArticlePurchaseTotalAmount(
      purchasePrice?.amount,
      purchasePrice?.taxPercentage
    )
  }

  getArticleStudentPurchaseTotal(): number | null {
    const purchasePrice = this.getArticlePurchasePrice()
    return calculateArticlePurchaseTotalAmount(
      purchasePrice?.studentAmount,
      purchasePrice?.taxPercentage
    )
  }

  hasStudentArticlePrice(): boolean {
    return (this.getArticleStudentPurchaseTotal() ?? 0) > 0
  }

  /**
   * Whether this visitor is actually charged the student rate. The rate itself
   * is advertised to everyone, so this has to come from the server rather than
   * from the presence of a student amount.
   */
  studentPricingApplies(): boolean {
    return this.getArticlePurchasePrice()?.studentPricingApplies === true
  }

  canPurchaseArticle(): boolean {
    return !!this.getArticlePurchaseTotal()
  }

  scrollToElement(elementId: string, event?: Event): void {
    if (event) {
      event.preventDefault()
    }

    // Skip if not in a browser environment
    if (!isPlatformBrowser(this.platformId)) {
      return
    }

    const element = this.document.getElementById(elementId)
    if (element) {
      // Scroll to element
      element.scrollIntoView({ behavior: 'smooth', block: 'center' })
      element.classList.add('footnote-highlight')
      setTimeout(() => {
        element.classList.remove('footnote-highlight')
      }, 2500)
    }
  }

  handleFootnoteClick(event: Event): void {
    const target = event.target as HTMLElement
    const footnoteLink = target.closest('a[href^="#footnote-"]')

    if (footnoteLink) {
      event.preventDefault()
      const footnoteId = footnoteLink.getAttribute('href')?.replace('#', '')
      if (footnoteId) {
        this.scrollToElement(footnoteId, event)
      }
    }
  }

  handleFootnoteItemClick(event: Event, referenceId: string): void {
    const target = event.target as HTMLElement
    if (!target.closest('a')) {
      this.scrollToElement(referenceId, event)
    }
  }

  private proceedWithDownload(): void {
    if (!this.articleDetails) {
      return
    }

    // Skip if not in a browser environment
    if (!isPlatformBrowser(this.platformId)) {
      return
    }

    this.loading = true
    this.changeDetectorRef.markForCheck()

    this.articlesService.downloadArticle(this.articleDetails.id).subscribe({
      next: blob => {
        const url = window.URL.createObjectURL(blob)
        const link = this.document.createElement('a')
        link.href = url
        const pdf = this.articleDetails?.pdf
        const extension = this.isFullUrl(pdf)
          ? 'pdf'
          : (pdf?.split('.').pop() ?? 'pdf')
        link.download = `${this.articleDetails?.title ?? 'article'}.${extension}`
        link.click()
        window.URL.revokeObjectURL(url)
        this.loading = false
        this.changeDetectorRef.markForCheck()
      },
      complete: () => {
        this.loading = false
        this.changeDetectorRef.markForCheck()
      },
      error: () => {
        this.showError('Failed to download article')
        this.loading = false
        this.changeDetectorRef.markForCheck()
      },
    })
  }

  private isFullUrl(url: string | undefined): boolean {
    return url?.startsWith('https://') ?? false
  }

  private updateViewportState(): void {
    if (!isPlatformBrowser(this.platformId)) {
      this.isDesktopBrowser = false
      return
    }

    this.isDesktopBrowser = window.innerWidth >= 1024
    this.changeDetectorRef.markForCheck()
  }

  private refreshIndexState(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return
    }

    window.requestAnimationFrame(() => {
      this.articleIndex()?.onScroll()
    })
  }

  getArticle(): ArticleWithNullableRefs | undefined {
    if (this.articleDetails) {
      return this.articleDetails as never
    }
    return
  }

  /** Shows an error message and clears it after a delay */
  private showError(message: string): void {
    this.error = message
    this.loading = false
    setTimeout(() => {
      this.error = null
      this.changeDetectorRef.markForCheck()
    }, 5000)
  }
}
